import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ProgressBar, StatusBadge } from "@/components/Badges";
import BookHeatmap from "@/components/BookHeatmap";

const fmt = (v) => `${Math.round(Number(v || 0) * 100)}%`;

export default function CountryDetail() {
  const { countryId } = useParams();
  const [data, setData] = useState(null);
  const [projectsByCluster, setProjectsByCluster] = useState({});
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [countryRes, booksRes] = await Promise.all([
        api.get(`/countries/${countryId}`),
        api.get(`/books`),
      ]);
      setData(countryRes.data);
      setBooks(booksRes.data.books);
      const map = {};
      await Promise.all(
        countryRes.data.clusters.map(async (cl) => {
          const r = await api.get(`/clusters/${cl.id}/projects`);
          map[cl.id] = r.data;
        })
      );
      setProjectsByCluster(map);
      setLoading(false);
    })();
  }, [countryId]);

  if (loading || !data) return <div className="p-10 mono text-xs text-[color:var(--ink-2)]">Loading…</div>;

  const { country, region, clusters } = data;

  return (
    <div className="max-w-[1600px] mx-auto px-10 py-10 fade-in-up" data-testid="country-detail-page">
      <nav className="mono text-[11px] uppercase tracking-widest text-[color:var(--ink-2)] mb-4">
        <Link to="/regions" className="hover:underline">Regions</Link>
        <span className="mx-2">/</span>
        <Link to={`/regions/${region.id}`} className="hover:underline">{region.name}</Link>
        <span className="mx-2">/</span>
        <span className="text-[color:var(--ink)]">{country.name}</span>
      </nav>

      <div className="mb-10">
        <h1 className="serif text-6xl tracking-tight leading-none">{country.name}</h1>
        <div className="mono text-[11px] text-[color:var(--ink-2)] mt-3 uppercase tracking-widest">
          {clusters.length} Cluster Project{clusters.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="space-y-10">
        {clusters.map((cluster) => (
          <div key={cluster.id} data-testid={`cluster-${cluster.name}`}>
            <div className="flex items-baseline gap-4 mb-4">
              <h2 className="serif text-3xl tracking-tight">{cluster.name}</h2>
              <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">
                Cluster Project
              </div>
            </div>

            <div className="hairline bg-white overflow-hidden">
              <table className="w-full data-table">
                <thead>
                  <tr>
                    <th>Language Project</th>
                    <th>Book Coverage (27 NT)</th>
                    <th>Overall</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(projectsByCluster[cluster.id] || []).map((p) => (
                    <tr
                      key={p.id}
                      data-testid={`project-row-${p.name}`}
                      className="cursor-pointer"
                      onClick={() => window.location.assign(`/projects/${p.id}`)}
                    >
                      <td className="w-52">
                        <Link
                          to={`/projects/${p.id}`}
                          className="text-base font-medium hover:underline"
                        >
                          {p.name}
                        </Link>
                      </td>
                      <td className="w-96 py-3">
                        <BookHeatmap books={books} progress={p.progress} size="sm" />
                      </td>
                      <td className="w-56">
                        <div className="flex items-center gap-3">
                          <div className="flex-1"><ProgressBar value={p.overall} /></div>
                          <span className="mono text-xs w-10 text-right">{fmt(p.overall)}</span>
                        </div>
                      </td>
                      <td><StatusBadge status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
